import { useTranslation } from 'react-i18next';
// frontend/src/hooks/useCart.js - CORREGIDO CON AUTO-REFRESH
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ecommerceApi, cartHelpers } from '../services/ecommerceApi';
import { useNotification } from '../components/common/GlobalNotifications';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { t } = useTranslation();
    const location = useLocation();
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [couponState, setCouponState] = useState({ status: 'none', message: '' });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const invalidCouponToastRef = useRef(false);
    const { showError } = useNotification();
    const shouldSkipCartLoad = /^\/(login|register|forgot-password|join-company)(\/|$)/.test(location.pathname);

    const buildCouponState = useCallback((cartData, validation) => {
        if (!cartData?.coupon_code) {
            return { status: 'none', message: '' };
        }

        const warnings = validation?.warnings || [];
        const invalidWarning = warnings.find((warning) => /coupon|cupon/i.test(String(warning)));

        if (invalidWarning) {
            return {
                status: 'invalid',
                message: String(invalidWarning)
            };
        }

        return {
            status: 'applied',
            message: t('hardcoded.hooks_usecart.cupon_coupon_code_aplicado', { coupon_code: cartData.coupon_code })
        };
    }, []);

    const loadCart = useCallback(async () => {
        if (shouldSkipCartLoad) {
            setCart(null);
            setError(null);
            setLoading(false);
            return;
        }

        try {
            let response = await ecommerceApi.cart.get();
            let nextCart = response.data;
            let validation = null;

            try {
                const validationResponse = await ecommerceApi.cart.validate();
                validation = validationResponse.data;
            } catch (validationError) {
                console.warn(t('hardcoded.hooks_usecart.cart_validation_unavailable'), validationError);
            }

            const nextCouponState = buildCouponState(nextCart, validation);

            if (nextCouponState.status === 'invalid' && nextCart?.coupon_code) {
                if (!invalidCouponToastRef.current) {
                    showError(t('hardcoded.hooks_usecart.el_cupon_aplicado_dejo_de'));
                    invalidCouponToastRef.current = true;
                }
                await ecommerceApi.cart.removeCoupon();
                response = await ecommerceApi.cart.get();
                nextCart = response.data;
                setCouponState({ status: 'none', message: '' });
            } else {
                invalidCouponToastRef.current = false;
                setCouponState(nextCouponState);
            }

            console.log(t('hardcoded.hooks_usecart.cart_loaded'), nextCart);
            setCart(nextCart);
            setError(null);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403 || err.response?.status === 404) {
                setCart(null);
                setError(null);
            } else {
                console.error(t('hardcoded.hooks_usecart.error_loading_cart'), err);
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, [buildCouponState, shouldSkipCartLoad, showError]);

    useEffect(() => {
        setLoading(true);
        loadCart();
    }, [loadCart]);

    const addToCart = async (item) => {
        try {
            console.log('🛒 Adding to cart:', item);
            const response = await ecommerceApi.cart.add(item);
            
            // ✅ CRÍTICO: Recargar carrito después de agregar
            await loadCart();
            
            return { success: true, data: response.data };
        } catch (err) {
            console.error(t('hardcoded.components_store_productcard.error_adding_to_cart'), err);
            const errorMsg = err.response?.data?.detail || t('hardcoded.components_store_productcard.error_al_agregar_al_carrito');
            showError(errorMsg);
            throw err;
        }
    };

    const updateQuantity = async (itemId, quantity) => {
        try {
            console.log('📝 Updating quantity:', { itemId, quantity });
            const response = await ecommerceApi.cart.updateItem(itemId, quantity);
            
            // ✅ CRÍTICO: Recargar carrito después de actualizar
            await loadCart();
            
            return { success: true, data: response.data };
        } catch (err) {
            console.error(t('hardcoded.hooks_usecart.error_updating_quantity'), err);
            return { success: false, error: err.response?.data?.detail || t('hardcoded.components_store_cartdrawer.error_al_actualizar_cantidad') };
        }
    };

    const removeItem = async (itemId) => {
        try {
            console.log('🗑️ Removing item:', itemId);
            await ecommerceApi.cart.removeItem(itemId);
            
            // ✅ CRÍTICO: Recargar carrito después de eliminar
            await loadCart();
            
            return { success: true };
        } catch (err) {
            console.error(t('hardcoded.hooks_usecart.error_removing_item'), err);
            return { success: false, error: err.response?.data?.detail || t('hardcoded.hooks_usecart.error_al_eliminar_item') };
        }
    };

    const applyCoupon = async (couponCode) => {
        try {
            const response = await ecommerceApi.cart.applyCoupon(couponCode);
            
            // ✅ Recargar carrito
            await loadCart();

            return { success: true, data: response.data };
        } catch (err) {
            const errorMsg = err.response?.data?.detail || t('hardcoded.hooks_usecart.cupon_invalido');
            showError(errorMsg);
            throw new Error(errorMsg);
        }
    };

    const removeCoupon = async () => {
        try {
            await ecommerceApi.cart.removeCoupon();
            
            // ✅ Recargar carrito
            await loadCart();
            
            return { success: true };
        } catch (err) {
            console.error(t('hardcoded.hooks_usecart.error_removing_coupon'), err);
            throw err;
        }
    };

    const validateCart = async () => {
        try {
            const response = await ecommerceApi.cart.validate();
            return {
                valid: !!response.data?.valid,
                errors: response.data?.errors || [],
                warnings: response.data?.warnings || []
            };
        } catch (err) {
            return {
                valid: false,
                errors: [err.response?.data?.detail || t('hardcoded.hooks_usecart.no_fue_posible_validar_el')],
                warnings: []
            };
        }
    };

    const clearCart = async () => {
        try {
            await ecommerceApi.cart.clear();
            
            // ✅ Recargar carrito
            await loadCart();
            
            return { success: true };
        } catch (err) {
            console.error(t('hardcoded.hooks_usecart.error_clearing_cart'), err);
            throw err;
        }
    };

    const isInCart = useCallback((id, type = 'product') => {
    if (!cart?.items) return false;

    if (type === 'kit') {
        return cart.items.some(item => String(item.kit_id || item.kit?.id) === String(id));
    }
    return cart.items.some(item => String(item.product_id || item.product?.id) === String(id));
    }, [cart]);

    const getCartItemQuantity = useCallback((id, type = 'product') => {
    if (!cart?.items) return 0;

    const item = type === 'kit'
        ? cart.items.find(item => String(item.kit_id || item.kit?.id) === String(id))
        : cart.items.find(item => String(item.product_id || item.product?.id) === String(id));

    return item ? item.quantity : 0;
    }, [cart]);



    const cartTotals = cart ? {
        ...(cartHelpers.computeCartTotals(cart) || {}),
        couponState
    } : null;

    const value = {
        cart,
        loading,
        error,
        loadCart,
        addToCart,
        updateQuantity,
        removeItem,
        applyCoupon,
        removeCoupon,
        clearCart,
        validateCart,
        isInCart,
        getCartItemQuantity,
        cartTotals,
        drawerOpen,
        openCartDrawer: () => setDrawerOpen(true),
        closeCartDrawer: () => setDrawerOpen(false),
        toggleCartDrawer: () => setDrawerOpen((prev) => !prev)
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
  const { t } = useTranslation();
    const context = useContext(CartContext);
    if (!context) {
        throw new Error(t('hardcoded.hooks_usecart.usecart_debe_ser_usado_dentro'));
    }
    return context;
};